import {ItemQuantity} from "./Inventory";

export type CraftingRecipe = {
    id: string;
    name: string;
    result: ItemQuantity;
    ingredients: ItemQuantity[];
    durationMs: number;
    description?: string;
    assemblyDurationMs?: number;
    assemblyId?: string;
    availableInAssembly?: boolean;
};

export const CRAFTING_RECIPES: CraftingRecipe[] = [
    {
        id: "craft-smelter1",
        name: "Stone Furnace",
        result: {itemId: "smelter1", quantity: 1},
        ingredients: [{itemId: "stone", quantity: 5}],
        durationMs: 3000,
        description: "Smelts raw resources into usable products.",
    },
    {
        id: "craft-iron-gear",
        name: "Iron Gear",
        result: {itemId: "ironGear", quantity: 1},
        ingredients: [{itemId: "ironplate", quantity: 2}],
        durationMs: 1500,
        assemblyDurationMs: 1200,
        assemblyId: "assembly-iron-gear",
        description: "Stamp iron plates into gears for machines.",
    },
    {
        id: "craft-copper-wire",
        name: "Copper Wire",
        result: {itemId: "copperWire", quantity: 2},
        ingredients: [{itemId: "copperplate", quantity: 1}],
        durationMs: 1200,
        assemblyDurationMs: 1000,
        assemblyId: "assembly-copper-wire",
        description: "Spool copper plates into wire automatically.",
    },
    {
        id: "craft-iron-stick",
        name: "Iron Stick",
        result: {itemId: "ironStick", quantity: 2},
        ingredients: [{itemId: "ironplate", quantity: 1}],
        durationMs: 900,
        description: "A meaningless intermediary product.",
    },
    {
        id: "craft-iron-pipe",
        name: "Iron Pipe",
        result: {itemId: "ironPipe", quantity: 1},
        ingredients: [{itemId: "ironplate", quantity: 2}],
        durationMs: 1200,
        description: "Used in items that deal with fluids.",
    },
    {
        id: "craft-water-pump",
        name: "Water Pump",
        result: {itemId: "waterPump", quantity: 1},
        ingredients: [
            {itemId: "ironPipe", quantity: 5},
            {itemId: "ironGear", quantity: 5},
        ],
        durationMs: 2500,
        description: "Pumps water from a source into a fluid system.",
    },
    {
        id: "craft-water-boiler",
        name: "Water Boiler",
        result: {itemId: "waterBoiler", quantity: 1},
        ingredients: [
            {itemId: "smelter1", quantity: 1},
            {itemId: "ironGear", quantity: 5},
            {itemId: "ironPipe", quantity: 5},
        ],
        durationMs: 3500,
        description: "Turns water into steam.",
    },
    {
        id: "craft-steam-generator",
        name: "Basic Steam Power Generator",
        result: {itemId: "steamGenerator", quantity: 1},
        ingredients: [
            {itemId: "ironPipe", quantity: 5},
            {itemId: "ironGear", quantity: 5},
            {itemId: "ironplate", quantity: 10},
        ],
        durationMs: 3500,
        description: "Consumes steam to provide base-level electricity.",
    },
    {
        id: "craft-electronic-circuit",
        name: "Electronic Circuit",
        result: {itemId: "electronicCircuit", quantity: 1},
        ingredients: [
            {itemId: "ironplate", quantity: 1},
            {itemId: "copperWire", quantity: 3},
        ],
        durationMs: 2500,
        assemblyDurationMs: 1500,
        assemblyId: "assembly-electronic-circuit",
        description: "Feed copper wire and iron plates to craft circuits.",
    },
    {
        id: "craft-miner1",
        name: "Burner Miner Drill",
        result: {itemId: "miner1", quantity: 1},
        ingredients: [
            {itemId: "ironGear", quantity: 3},
            {itemId: "ironplate", quantity: 3},
            {itemId: "smelter1", quantity: 1},
        ],
        durationMs: 4000,
        description: "Fuels itself with coal to mine ore automatically.",
    },
    {
        id: "craft-steel-furnace",
        name: "Steel Furnace",
        result: {itemId: "steelFurnace", quantity: 1},
        ingredients: [
            {itemId: "steelPlate", quantity: 5},
            {itemId: "stoneBrick", quantity: 5},
        ],
        durationMs: 4500,
        description: "A faster furnace built from steel and bricks.",
    },
    {
        id: "craft-transport-belt",
        name: "Transport Belt",
        result: {itemId: "transportBelt", quantity: 2},
        ingredients: [
            {itemId: "ironplate", quantity: 1},
            {itemId: "ironGear", quantity: 1},
            {itemId: "ironStick", quantity: 1},
        ],
        durationMs: 2000,
        description: "Moves items along a lane for hands-free logistics.",
    },
    {
        id: "craft-inserter",
        name: "Inserter",
        result: {itemId: "inserter", quantity: 1},
        ingredients: [
            {itemId: "ironplate", quantity: 1},
            {itemId: "ironGear", quantity: 1},
            {itemId: "electronicCircuit", quantity: 1},
        ],
        durationMs: 2500,
        description: "Mechanical arm that moves items between belts and machines.",
    },
    {
        id: "craft-science-pack1",
        name: "Science Pack 1",
        result: {itemId: "sciencePack1", quantity: 1},
        ingredients: [
            {itemId: "copperplate", quantity: 2},
            {itemId: "ironGear", quantity: 2},
        ],
        durationMs: 3500,
        description: "Basic research consumable for early technologies.",
    },
    {
        id: "craft-science-pack2",
        name: "Intermediate Science Pack",
        result: {itemId: "sciencePack2", quantity: 1},
        ingredients: [
            {itemId: "steelPlate", quantity: 1},
            {itemId: "electronicCircuit", quantity: 2},
        ],
        durationMs: 5000,
        description: "Advanced test tubes assembled from steel and circuits.",
    },
    {
        id: "craft-circuit-assembler",
        name: "Circuit Assembler",
        result: {itemId: "circuitAssembler", quantity: 1},
        ingredients: [
            {itemId: "electronicCircuit", quantity: 3},
            {itemId: "ironGear", quantity: 3},
            {itemId: "ironplate", quantity: 3},
        ],
        durationMs: 5000,
        description: "Dedicated assembler that speeds up circuit production.",
    },
    {
        id: "craft-science-pack3",
        name: "Advanced Science Pack",
        result: {itemId: "sciencePack3", quantity: 2},
        ingredients: [
            {itemId: "advancedCircuit", quantity: 2},
            {itemId: "steelPlate", quantity: 1},
            {itemId: "ironGear", quantity: 2},
        ],
        durationMs: 8000,
        description: "High-tier research consumable requiring advanced circuits.",
    },
    {
        id: "craft-advanced-circuit",
        name: "Advanced Circuit",
        result: {itemId: "advancedCircuit", quantity: 1},
        ingredients: [
            {itemId: "electronicCircuit", quantity: 2},
            {itemId: "copperWire", quantity: 4},
            {itemId: "steelPlate", quantity: 1},
        ],
        durationMs: 6000,
        assemblyDurationMs: 4500,
        assemblyId: "assembly-advanced-circuit",
        description: "Multi-layer circuit board for advanced machinery.",
    },
    {
        id: "craft-battery",
        name: "Battery",
        result: {itemId: "battery", quantity: 1},
        ingredients: [
            {itemId: "ironplate", quantity: 1},
            {itemId: "copperplate", quantity: 2},
            {itemId: "electronicCircuit", quantity: 1},
        ],
        durationMs: 5000,
        assemblyDurationMs: 4000,
        assemblyId: "assembly-battery",
        description: "Energy storage cell for advanced electronics.",
    },
    {
        id: "craft-processing-unit",
        name: "Processing Unit",
        result: {itemId: "processingUnit", quantity: 1},
        ingredients: [
            {itemId: "advancedCircuit", quantity: 2},
            {itemId: "electronicCircuit", quantity: 5},
            {itemId: "battery", quantity: 1},
        ],
        durationMs: 10000,
        assemblyDurationMs: 8000,
        assemblyId: "assembly-processing-unit",
        description: "High-performance computing module for top-tier machines.",
    },
    {
        id: "craft-electric-miner",
        name: "Electric Miner Drill",
        result: {itemId: "miner2", quantity: 1},
        ingredients: [
            {itemId: "electronicCircuit", quantity: 3},
            {itemId: "ironGear", quantity: 5},
            {itemId: "ironplate", quantity: 10},
        ],
        durationMs: 4500,
        description: "Electric-powered drill that mines faster without fuel.",
    },
    {
        id: "craft-solar-panel",
        name: "Solar Panel",
        result: {itemId: "solarPanel", quantity: 1},
        ingredients: [
            {itemId: "copperplate", quantity: 5},
            {itemId: "electronicCircuit", quantity: 10},
            {itemId: "steelPlate", quantity: 3},
        ],
        durationMs: 10000,
        description: "Generates clean power from sunlight (40kW peak).",
    },
    {
        id: "craft-accumulator",
        name: "Accumulator",
        result: {itemId: "accumulator", quantity: 1},
        ingredients: [
            {itemId: "battery", quantity: 5},
            {itemId: "ironplate", quantity: 10},
            {itemId: "electronicCircuit", quantity: 5},
        ],
        durationMs: 8000,
        description: "Stores 100kW of electricity for nighttime discharge.",
    },
    {
        id: "craft-fast-assembler",
        name: "Fast Circuit Assembler",
        result: {itemId: "fastAssembler", quantity: 1},
        ingredients: [
            {itemId: "circuitAssembler", quantity: 1},
            {itemId: "electronicCircuit", quantity: 10},
            {itemId: "steelPlate", quantity: 5},
            {itemId: "ironGear", quantity: 10},
        ],
        durationMs: 8000,
        description: "Upgraded assembler with 1.5× speed and module slots.",
    },
    {
        id: "craft-electric-furnace",
        name: "Electric Furnace",
        result: {itemId: "electricFurnace", quantity: 1},
        ingredients: [
            {itemId: "steelFurnace", quantity: 1},
            {itemId: "advancedCircuit", quantity: 5},
            {itemId: "stoneBrick", quantity: 10},
        ],
        durationMs: 6000,
        description: "Coal-free smelting using electricity (1× speed, 50kW).",
    },
    {
        id: "craft-speed-module1",
        name: "Speed Module 1",
        result: {itemId: "speedModule1", quantity: 1},
        ingredients: [
            {itemId: "advancedCircuit", quantity: 5},
            {itemId: "electronicCircuit", quantity: 5},
        ],
        durationMs: 15000,
        description: "Increases machine speed by 20%, energy consumption by 50%.",
    },
    {
        id: "craft-productivity-module1",
        name: "Productivity Module 1",
        result: {itemId: "productivityModule1", quantity: 1},
        ingredients: [
            {itemId: "advancedCircuit", quantity: 5},
            {itemId: "electronicCircuit", quantity: 5},
        ],
        durationMs: 15000,
        description: "Grants +4% free outputs, reduces speed by 5%, increases energy by 40%.",
    },
    {
        id: "craft-efficiency-module1",
        name: "Efficiency Module 1",
        result: {itemId: "efficiencyModule1", quantity: 1},
        ingredients: [
            {itemId: "advancedCircuit", quantity: 5},
            {itemId: "electronicCircuit", quantity: 5},
        ],
        durationMs: 15000,
        description: "Reduces machine energy consumption by 30% (minimum 20%).",
    },
];
